// src/components/RoutePanel.tsx (v2.18)
// 入院経路分析タブ: 新規入院 (047_adm_henkansumi) の経路を多軸で分析
//
// データソース: /api/query/route → fact_route (粒度×date×診療科×医療区分×経由×予定緊急×紹介×count)
//
// 構成:
//   ① RouteKpiCards   — 当月: 新規入院数 / 予定入院率 / 病院経由率 / 紹介入院率 (前年比つき)
//   ② RouteAxisTable  — 軸切替 [予定緊急 / 紹介 / 経由 / 経路8] × 月次 + 月平均 + 構成比% + 前年比
//   ③ RouteMixStacked — 月次 経路 8 セグメント 積上 (件数 / 構成比 toggle)
//   ④ RouteDeptTable  — 科 × (軸) heatmap風 集計表 + ランキング
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { api } from "../lib/api";
import { useFilters } from "../store";
import { PlotFigure } from "./Plot";
import { CsvButton } from "./CsvButton";
import { fmtInt, fmtPct } from "../lib/format";
import { fmtMonthJp, buildTimeAxis, buildMonthAxisMark } from "../lib/chartAxis";
import { fiscalYearOf, FY_MONTHS } from "../lib/fiscalYear";
import { useSort, SortTh } from "../lib/useSort";
import type { RouteResponse, RouteRow } from "@shared/api";

// ── 軸選択 ────────────────────────────────────────────────────────────
type AxisKey = "予定緊急" | "紹介" | "経由" | "経路" | "経路7";

// 経路カテゴリ (2026-05-23 ユーザ要望): 病院 / 総CL / その他 (= 腎CL + 地域ケア統合) × 予定/緊急 = 6 カテゴリ。
// 経由(HP/CL/腎CL/地域ケア) × 予定緊急(予定/緊急) を 6 グルーピングに集約:
//   経由     予定緊急   集約カテゴリ
//   HP       予定       病院予定
//   HP       緊急       病院緊急
//   CL       予定       総CL予定
//   CL       緊急       総CL緊急
//   腎CL     予定       その他予定
//   腎CL     緊急       その他緊急
//   地域ケア 予定       その他予定
//   地域ケア 緊急       その他緊急
const KEIYU_TO_GROUP: Record<string, string> = {
  "HP": "病院", "CL": "総CL", "腎CL": "その他", "地域ケア": "その他",
};

const AXIS_DEFS: Array<{ key: AxisKey; label: string; categories: string[] }> = [
  { key: "予定緊急", label: "予定 / 緊急", categories: ["予定", "緊急"] },
  { key: "紹介",     label: "紹介 / 非紹介", categories: ["紹介あり", "紹介なし"] },
  { key: "経由",     label: "経由 (病院/総CL/その他=腎CL+地域ケア)", categories: ["病院", "総CL", "その他"] },
  { key: "経路",     label: "経路(6区分: 経由 × 予定/緊急)",
    categories: ["病院予定", "病院緊急", "総CL予定", "総CL緊急", "その他予定", "その他緊急"] },
  // 経路7 (2026-06-03 ユーザ要望): 経路6 (経由グループ × 予定/緊急) を さらに 紹介有無 で分割 = 12 カテゴリ。
  { key: "経路7",    label: "経路(12区分: 経由 × 予定/緊急 × 紹介)",
    categories: [
      "病院予定・紹介", "病院予定・非紹介", "病院緊急・紹介", "病院緊急・非紹介",
      "総CL予定・紹介", "総CL予定・非紹介", "総CL緊急・紹介", "総CL緊急・非紹介",
      "その他予定・紹介", "その他予定・非紹介", "その他緊急・紹介", "その他緊急・非紹介",
    ] },
];

// 各軸の色 (2026-05-23 ユーザ採用: パターン B "Clean Bright"、 5 案比較で決定)
// 設計方針:
//   - 経路 6 軸: 経由 = hue (病院=emerald / 総CL=royal blue / その他=stone)、
//                予定 = 濃色 / 緊急 = 明色 (淡パステル) で 3 ペア
//                予定側を濃く 緊急側を薄く = 計画的入院を視覚的に土台として強調
//                赤と amber は経路軸では使わない (警告色中立化)
//   - 経由 軸 (3 値): 経路 6 の濃色側 (= 予定側) を 3 経由の代表色として再利用 → 軸間で色一貫
//   - 予定緊急 2 値軸: 既存 SurgeryPanel と整合 (#1B9E77 緑 / #dc2626 赤)
//   - 紹介 軸: indigo-600 (紹介あり) / slate (紹介なし)
const AXIS_COLORS: Record<AxisKey, string[]> = {
  "予定緊急": ["#1B9E77", "#dc2626"],
  "紹介":     ["#4f46e5", "#64748b"],
  "経由":     ["#047857", "#1e40af", "#57534e"],
  "経路": [
    "#047857", "#6ee7b7",   // 病院予定 / 病院緊急 (emerald-700 / emerald-300)
    "#1e40af", "#93c5fd",   // 総CL予定 / 総CL緊急 (blue-800 / blue-300)
    "#57534e", "#d6d3d1",   // その他予定 / その他緊急 (stone-700 / stone-300、 = 腎CL + 地域ケア 統合)
  ],
  // 経路 7: 経路6 の 経由 hue を継承。 各 経由ごとに 予定紹介(濃)→予定非紹介→緊急紹介→緊急非紹介(淡) の 4 段。
  "経路7": [
    "#065f46", "#34d399", "#6ee7b7", "#d1fae5",   // 病院: 予定紹介/予定非紹介/緊急紹介/緊急非紹介 (emerald 800/400/300/100)
    "#1e3a8a", "#3b82f6", "#93c5fd", "#dbeafe",   // 総CL: 同順 (blue 900/500/300/100)
    "#44403c", "#78716c", "#a8a29e", "#e7e5e4",   // その他: 同順 (stone 800/500/400/200)
  ],
};

// segment 内ラベル文字色: 各カテゴリ色の relative luminance > 閾値 なら濃色文字、 さもなくば白文字。
// 明色 (#14b8a6 / #f59e0b / #38bdf8) で白文字 contrast が落ちる問題への対応。
function labelColorOf(axis: AxisKey, cat: string): string {
  const cats = AXIS_DEFS.find((a) => a.key === axis)!.categories;
  const idx = cats.indexOf(cat);
  const bg = AXIS_COLORS[axis][idx] ?? "#000";
  return contrastTextColor(bg);
}
function contrastTextColor(hex: string): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) return "white";
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  // sRGB → linear、 relative luminance (WCAG)。
  // 閾値 0.30: teal-500 (L=0.375), amber-500 (L=0.438), sky-400 (L≈0.55) は dark text、
  // teal-700 / amber-700 / violet-700/800 / sky-700 は white text。 視認性 score を画面 zoom で目視確認済 (2026-05-23)。
  const toLin = (v: number) => v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
  return L > 0.30 ? "#1f2937" : "white";
}

// row → カテゴリ抽出 (経由/経路 は 病院/総CL/その他 の 3 グループに集約)
function categoryOf(axis: AxisKey, r: RouteRow): string {
  switch (axis) {
    case "予定緊急": return r.予定緊急;
    case "紹介":     return r.紹介 === 1 ? "紹介あり" : "紹介なし";
    case "経由":     return KEIYU_TO_GROUP[r.経由] ?? r.経由;
    case "経路":     return `${KEIYU_TO_GROUP[r.経由] ?? r.経由}${r.予定緊急}`;
    case "経路7":    return `${KEIYU_TO_GROUP[r.経由] ?? r.経由}${r.予定緊急}・${r.紹介 === 1 ? "紹介" : "非紹介"}`;
  }
}

// 期間 [from, to] (inclusive、 月単位) の月初リストを生成
function enumerateMonths(from: string, to: string): string[] {
  const out: string[] = [];
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  let y = fy, m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}-01`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

// 末尾12ヶ月移動平均 (直近12ヶ月平均トグル用)。
//   avgMode=false: 単月値 (present(mo) でなければ null)。
//   avgMode=true : allMonths 上で mo の直近12ヶ月のうち present な月だけの getter 平均。
function trailing12(
  mo: string, allMonths: string[], avgMode: boolean,
  present: (m: string) => boolean, get: (m: string) => number,
): number | null {
  if (!avgMode) return present(mo) ? get(mo) : null;
  const i = allMonths.indexOf(mo);
  const win = allMonths.slice(Math.max(0, i - 11), i + 1).filter(present);
  return win.length ? win.reduce((s, m) => s + get(m), 0) / win.length : null;
}

// ─────────────────────────────────────────────────────────────────────
// メイン
// ─────────────────────────────────────────────────────────────────────
export function RoutePanel() {
  const f = useFilters();
  // [2026-08-25] サイドバー「経由」フィルタ (病院/総CL/その他)。scopeLabel にも合成表示。
  const scopeLabel = [
    f.focusedDept,
    f.routeKeiyu !== "合計" ? `${f.routeKeiyu}経由` : null,
    f.yotei !== "合計" ? `${f.yotei}入院` : null,
  ]
    .filter(Boolean).join(" ・ ") || "病院全体";
  const [axis, setAxis] = useState<AxisKey>("経路");
  const [stackMode, setStackMode] = useState<"件数" | "構成比">("件数");

  // 期間は filter の periodFrom 〜 asOfMonth。前年比較に加え、年度比較9カード用に
  // 前年度4月まで必ず取得する (開始月を途中月へ動かしても4月始まりの線を欠かさない)。
  const periodFromForRoute = useMemo(() => {
    const fy = fiscalYearOf(f.periodTo);
    const prevFyStart = `${fy - 1}-04-01`;
    const [y, m] = f.periodFrom.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1 - 12, 1));
    const shifted = dt.toISOString().slice(0, 10);
    return shifted < prevFyStart ? shifted : prevFyStart;
  }, [f.periodFrom, f.periodTo]);

  const { data, isLoading } = useQuery<RouteResponse>({
    queryKey: ["route", f.granularity, f.iryo, f.focusedDept, periodFromForRoute, f.periodTo],
    queryFn: () => api.route({
      granularity: f.granularity,
      iryo: f.iryo,
      periodFrom: periodFromForRoute,
      periodTo: f.periodTo,
      focusedDept: f.focusedDept ?? undefined,
      deptBreakdown: true,
    }),
    placeholderData: (prev) => prev,
  });

  // 表示期間: 要求期間 (filter.periodFrom 〜 periodTo) の全月を生成。
  //   データ欠損月 (keiro 未取得、 focusedDept 患者ゼロ) も月リストに含めて 0 扱い。
  //   Codex review M1: anchor scope の rows 依存だと欠損月が消えて期間が縮む問題への対応。
  const displayMonths = useMemo(
    () => enumerateMonths(f.periodFrom, f.periodTo),
    [f.periodFrom, f.periodTo]
  );
  // データが実在する月の集合 (= keiro データある月)。
  //
  // Codex 2 周目 H: anchor scope (data.rows) だけで判定すると、 focused dept で
  //   その月に新規入院がない (= 行ゼロ) ケースを「keiro 未取得月」と誤判定する。
  //   修正: byDept も含めて「いずれかの科に行がある月」を keiro データ取得済月とする。
  //   byDept は deptBreakdown=true で常時取得しているので必ず存在。
  const monthsWithData = useMemo(() => {
    if (!data) return new Set<string>();
    const s = new Set<string>(data.rows.map((r) => r.date));
    (data.byDept ?? []).forEach((r) => s.add(r.date));
    return s;
  }, [data]);
  // 欠損月 (= 要求期間内だが keiro データなし)
  const missingMonths = useMemo(
    () => displayMonths.filter((d) => !monthsWithData.has(d)),
    [displayMonths, monthsWithData]
  );
  // 当月 (filter の asOfMonth が displayMonths にあればそれ、 なければ最新月)
  const asOfMonth = useMemo(() => {
    const hit = displayMonths.find((d) => d.slice(0, 7) === f.asOfMonth.slice(0, 7));
    return hit ?? (displayMonths.length > 0 ? displayMonths[displayMonths.length - 1] : f.asOfMonth);
  }, [displayMonths, f.asOfMonth]);

  // サイドバー「経由」「予定緊急」フィルタを適用した scope。
  //   monthsWithData/missingMonths (keiro データ取得可否) は絞り込み前の data のまま判定する
  //   (= 選択区分の新規入院がゼロの月を「データ未取得」と誤判定しないため)。
  const scopedData = useMemo<RouteResponse | undefined>(() => {
    if (!data) return data;
    const keep = (r: RouteRow) =>
      (f.routeKeiyu === "合計" || (KEIYU_TO_GROUP[r.経由] ?? r.経由) === f.routeKeiyu) &&
      (f.yotei === "合計" || r.予定緊急 === f.yotei);
    return { ...data, rows: data.rows.filter(keep), byDept: (data.byDept ?? []).filter(keep) };
  }, [data, f.routeKeiyu, f.yotei]);

  // 月 → カテゴリ → 件数 (anchor scope の monthly cross-tab)
  const monthlyByCat = useMemo(() => {
    if (!scopedData) return new Map<string, Map<string, number>>();
    const m = new Map<string, Map<string, number>>();
    for (const r of scopedData.rows) {
      const cat = categoryOf(axis, r);
      if (!m.has(r.date)) m.set(r.date, new Map());
      const cm = m.get(r.date)!;
      cm.set(cat, (cm.get(cat) ?? 0) + r.count);
    }
    return m;
  }, [scopedData, axis]);

  // 月合計
  const monthTotals = useMemo(() => {
    const t = new Map<string, number>();
    monthlyByCat.forEach((cm, d) => {
      let s = 0; cm.forEach((v) => s += v);
      t.set(d, s);
    });
    return t;
  }, [monthlyByCat]);

  // 入院経路は入院実績と同じ年度内の開始月/終了月で期間を選ぶ。
  // 月平均は下端の診療科時系列表が選択期間から直接計算するため、各月セルは常に単月値。
  const avgMode = false;
  const allMonths = useMemo(
    () => enumerateMonths(periodFromForRoute, f.periodTo),
    [periodFromForRoute, f.periodTo]
  );

  return (
    <div className="space-y-3">
      {/* 期間欠損 / 適用中フィルタの案内 */}
      <RouteNotices
        missingMonths={missingMonths}
        totalMonths={displayMonths.length}
        yotei={f.yotei}
        routeKeiyu={f.routeKeiyu}
      />

      {/* 印刷 1 枚目 = KPI カード 1 行 + 年度推移 9 枚 (3×3)。以降の表は 2 枚目から
          (2026-09-18 ユーザ指示)。★強制改ページは **付けない** — 理由は styles.css の
          `.route-print-page-1` のコメント (白紙ページを生むうえ、そもそも不要)。
          screen では space-y-3 で従来の間隔を保つ。 */}
      <div className="route-print-page-1 space-y-3">
        <RouteKpiCards
          data={scopedData} months={displayMonths} scopeLabel={scopeLabel}
          monthsWithData={monthsWithData}
        />

        <RouteFiscalYearGrid data={scopedData} asOfMonth={asOfMonth} scopeLabel={scopeLabel} />
      </div>

      <div className="print-page-break" aria-hidden />

      <RouteDeptTable
        data={scopedData} months={displayMonths} monthsWithData={monthsWithData} axis={axis}
        categories={AXIS_DEFS.find(a => a.key === axis)!.categories}
      />

      <div className="print-page-break" aria-hidden />

      <RouteDeptMonthlyTable
        data={scopedData}
        months={displayMonths}
        monthsWithData={monthsWithData}
        scopeLabel={scopeLabel}
      />

      <div className="print-page-break" aria-hidden />

      <RouteAxisTable
        axis={axis} setAxis={setAxis}
        monthlyByCat={monthlyByCat} monthTotals={monthTotals}
        months={displayMonths} monthsWithData={monthsWithData}
        asOfMonth={asOfMonth} avgMode={avgMode} allMonths={allMonths}
        scopeLabel={scopeLabel} sqlMs={scopedData?.sqlMs ?? null} isLoading={isLoading}
      />

      <div className="print-page-break" aria-hidden />

      <RouteMixStackedBar
        axis={axis} categories={AXIS_DEFS.find(a => a.key === axis)!.categories}
        colors={AXIS_COLORS[axis]}
        monthlyByCat={monthlyByCat} monthTotals={monthTotals}
        months={displayMonths} monthsWithData={monthsWithData}
        avgMode={avgMode} allMonths={allMonths}
        stackMode={stackMode} setStackMode={setStackMode}
        scopeLabel={scopeLabel}
      />

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 年度比較 9カード: 行=経由、列=予定緊急。4月〜3月を同じ x 位置で前年比較する。
// ─────────────────────────────────────────────────────────────────────
type RouteFyScope = "病院全体" | "HP経由" | "CL経由";
type RouteFyKind = "全体" | "予定入院" | "緊急入院";
type RouteFyPoint = { month: number; value: number; series: "今年度" | "前年度"; date: string };

const ROUTE_FY_SCOPES: RouteFyScope[] = ["病院全体", "HP経由", "CL経由"];
const ROUTE_FY_KINDS: RouteFyKind[] = ["全体", "予定入院", "緊急入院"];

function routeFyDate(fy: number, month: number): string {
  return `${month >= 4 ? fy : fy + 1}-${String(month).padStart(2, "0")}-01`;
}

function RouteFiscalYearGrid({ data, asOfMonth, scopeLabel }: {
  data: RouteResponse | undefined; asOfMonth: string; scopeLabel: string;
}) {
  const fy = fiscalYearOf(asOfMonth);
  const values = useMemo(() => {
    const out = new Map<string, number>();
    for (const r of data?.rows ?? []) {
      for (const scope of ROUTE_FY_SCOPES) {
        if (scope === "HP経由" && r.経由 !== "HP") continue;
        if (scope === "CL経由" && r.経由 !== "CL") continue;
        for (const kind of ROUTE_FY_KINDS) {
          if (kind === "予定入院" && r.予定緊急 !== "予定") continue;
          if (kind === "緊急入院" && r.予定緊急 !== "緊急") continue;
          const key = `${scope}\t${kind}\t${r.date}`;
          out.set(key, (out.get(key) ?? 0) + r.count);
        }
      }
    }
    return out;
  }, [data]);

  const hasDate = useMemo(() => new Set((data?.rows ?? []).map((r) => r.date)), [data]);

  const buildPoints = (scope: RouteFyScope, kind: RouteFyKind): RouteFyPoint[] => {
    const out: RouteFyPoint[] = [];
    for (const [year, series] of [[fy - 1, "前年度"], [fy, "今年度"]] as const) {
      FY_MONTHS.forEach((month, monthIndex) => {
        const date = routeFyDate(year, month);
        // 未来月・経路データ自体が未取得の月は 0 ではなく欠損として線を止める。
        if (!hasDate.has(date)) return;
        out.push({ month: monthIndex, value: values.get(`${scope}\t${kind}\t${date}`) ?? 0, series, date });
      });
    }
    return out;
  };

  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">{scopeLabel} 新規入院数 年度推移（4月始まり）</h2>
        <span className="text-xs text-slate-500">行=経由 / 列=入院区分</span>
        <div className="ml-auto flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1"><span style={{ width: 14, height: 2, background: "#1B9E77", display: "inline-block" }} />{fy}年度</span>
          <span className="flex items-center gap-1"><span style={{ width: 14, height: 0, borderTop: "1.5px solid #94a3b8", display: "inline-block" }} />{fy - 1}年度</span>
        </div>
      </div>
      {/* route-fy-grid: 印刷でも 3 列 × 3 行 に固定する。
          ★A4 横なら `lg:` (1024px) は紙幅 1085px で効くので Tailwind だけでも 3 列になる。
          固定は保険: A4 縦 (743px) や余白を広げた設定では `sm:` の 2 列に落ちてしまう。 */}
      <div className="route-fy-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {ROUTE_FY_SCOPES.flatMap((scope) => ROUTE_FY_KINDS.map((kind) => (
          <RouteFiscalYearCard key={`${scope}-${kind}`} scope={scope === "病院全体" ? scopeLabel : scope} kind={kind} points={buildPoints(scope, kind)} fy={fy} />
        )))}
      </div>
    </section>
  );
}

// 印刷 (A4 横) 専用のグラフ実寸。画面用とは別に描き直すのでここが紙面の 1 枚目の高さを決める。
const ROUTE_FY_PRINT_W = 343;
const ROUTE_FY_PRINT_H = 155;

function RouteFiscalYearCard({ scope, kind, points, fy }: {
  scope: string; kind: RouteFyKind; points: RouteFyPoint[]; fy: number;
}) {
  const current = points.filter((d) => d.series === "今年度");
  const previous = points.filter((d) => d.series === "前年度");
  const tooltipRows = FY_MONTHS.map((month, i) => ({
    month: i,
    label: `${month}月`,
    current: current.find((d) => d.month === i)?.value ?? null,
    previous: previous.find((d) => d.month === i)?.value ?? null,
  })).filter((d) => d.current != null || d.previous != null);
  const fmt = (v: number | null) => v == null ? "—" : `${Math.round(v).toLocaleString()} 人`;
  const buildOptions = (width: number, height: number, withTip: boolean): Plot.PlotOptions => ({
    width, height,
    marginLeft: 48, marginRight: 6, marginTop: 6, marginBottom: 22,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "9px" },
    x: { label: null, domain: [-0.3, 11.3], ticks: [...Array(12).keys()], tickFormat: (i: number) => String(FY_MONTHS[i] ?? "") },
    y: { label: null, grid: true, nice: true, zero: false, tickFormat: (v: number) => Math.round(v).toLocaleString() },
    marks: [
      Plot.lineY(previous, { x: "month", y: "value", stroke: "#94a3b8", strokeWidth: 1.5, strokeOpacity: 0.7 }),
      Plot.dot(previous, { x: "month", y: "value", fill: "#94a3b8", fillOpacity: 0.7, r: 1.8 }),
      Plot.lineY(current, { x: "month", y: "value", stroke: "#1B9E77", strokeWidth: 2.4 }),
      Plot.dot(current, { x: "month", y: "value", fill: "#1B9E77", r: 2.4 }),
      ...(withTip ? [Plot.tip(tooltipRows, Plot.pointerX({
        x: "month", y: (d) => d.current ?? d.previous ?? 0, fontSize: 11,
        title: (d) => [`${d.label}`, `${fy}年度: ${fmt(d.current)}`, `${fy - 1}年度: ${fmt(d.previous)}`].join("\n"),
      }))] : []),
    ],
  });
  const screenOptions = buildOptions(460, 130, true);
  // ★印刷は画面の svg を CSS で縮めるのではなく、紙の枠に合わせて **描き直す**。
  //   理由: PlotFigure(responsiveWidth) は「画面で測った幅」で viewBox を作る。一方 Plot が
  //   svg に付ける既定 style は `max-width: 100%` = **縮むだけで伸びない**
  //   (node_modules/@observablehq/plot/src/plot.js)。つまり画面で測った幅がそのまま紙の
  //   上限になり、紙面がブラウザの窓幅に引きずられる。2026-09-18 実測
  //   (窓幅 → 画面の viewBox → 紙面 3 列 (枠 343px) での実寸):
  //     窓 1920 → 460×130 → 紙 343× 97  … 9px の文字が 6.7px まで縮む
  //     窓  700 → 203×130 → 紙 203×130  … 段組 343px の 4 割が空白のまま
  //   ★「あふれて 2 枚目へ割れる」のではない (縮むだけなので 1 枚には必ず収まる)。
  //   問題は **同じ画面から窓幅ごとに違う紙が出ること** と **紙を使い切れないこと**。
  //   紙側だけこのサイズで描けば、窓幅に依らず 343×155・文字は 9px のまま。
  //   サイズは A4 横の印刷領域 (≒1085×756px @96dpi) から逆算: 3 列 = 1 枚あたり幅 ≒ 343px、
  //   KPI 行 + 見出し + 枠を引いた残りを 3 行で割って高さ 155px。
  //   ★「固定」ではない: `max-width:100%` は紙側でも生きているので、**列幅が 343px を
  //   下回れば等比縮小する**（列幅 227px で 218×98 になるのを実測）。窓幅依存は消えたが
  //   **紙幅依存は残る**。A4 横の実際の列幅は 360.7px なので 343 には余裕があり、
  //   余白設定を少し広げても縮まない — この余裕を確保するために列幅ちょうどにしていない。
  const printOptions = buildOptions(ROUTE_FY_PRINT_W, ROUTE_FY_PRINT_H, false);
  return (
    <div className="route-fy-card border border-slate-200 rounded px-1.5 py-1 bg-slate-50/30 min-w-0">
      <div className="route-fy-card-title text-[11px] font-semibold text-slate-700 leading-tight">{scope} × {kind} <span className="text-slate-500 font-normal">(人)</span></div>
      {points.length === 0
        ? <div className="h-24 flex items-center justify-center text-xs text-slate-400">データなし</div>
        : (
          <>
            <PlotFigure className="route-fy-chart-screen" options={screenOptions} responsiveWidth />
            <PlotFigure className="route-fy-chart-print" options={printOptions} />
          </>
        )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// 警告 chip: keiro 未取得月 / yotei グローバルフィルタ非適用 (Codex M3/M4)
// ─────────────────────────────────────────────────────────────────────
function RouteNotices({
  missingMonths, totalMonths, yotei, routeKeiyu,
}: { missingMonths: string[]; totalMonths: number; yotei: string; routeKeiyu: string }) {
  if (missingMonths.length === 0 && yotei === "合計" && routeKeiyu === "合計") return null;
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      {routeKeiyu !== "合計" && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
          🚪 サイドバーの「経由={routeKeiyu}」で絞り込み中 — すべての集計・グラフがこの区分のみ
        </span>
      )}
      {missingMonths.length > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">
          ⚠ 期間 {totalMonths} ヶ月のうち <b className="mx-1">{missingMonths.length}</b> ヶ月が欠損
          <span className="text-amber-600">
            ({missingMonths.length <= 3
              ? missingMonths.map((d) => d.slice(0, 7)).join(", ")
              : `${missingMonths.slice(0, 3).map((d) => d.slice(0, 7)).join(", ")}…`})
          </span>
          <span className="text-amber-600">— keiro データ未取得月は 0 件扱い</span>
        </span>
      )}
      {yotei !== "合計" && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
          🚪 サイドバーの「予定緊急={yotei}」で絞り込み中 — すべての集計・グラフがこの区分のみ
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ① KpiCards: 当月 4 指標
// ─────────────────────────────────────────────────────────────────────
function RouteKpiCards({
  data, months, scopeLabel, monthsWithData,
}: {
  data: RouteResponse | undefined; months: string[]; scopeLabel: string;
  monthsWithData: Set<string>;
}) {
  const cards = useMemo(() => {
    if (!data) return null;
    // 月別 集計 (総数/紹介/予定/HP)
    const byMonth = new Map<string, { total: number; shoukai: number; yotei: number; hp: number }>();
    for (const r of data.rows) {
      if (!byMonth.has(r.date)) byMonth.set(r.date, { total: 0, shoukai: 0, yotei: 0, hp: 0 });
      const x = byMonth.get(r.date)!;
      x.total += r.count;
      if (r.紹介 === 1) x.shoukai += r.count;
      if (r.予定緊急 === "予定") x.yotei += r.count;
      if (r.経由 === "HP") x.hp += r.count;
    }
    // 開始月〜終了月の月平均。前年も対応する同じ月範囲を12ヶ月ずらして比較する。
    // 経路データ未取得月は分母から除外し、取得済みだが選択条件が0件の月は0として含める。
    const calc = (targetMonths: string[]) => {
      const wmonths = targetMonths.filter((m) => monthsWithData.has(m));
      if (wmonths.length === 0) return null;
      let total = 0, shoukai = 0, yotei = 0, hp = 0;
      for (const m of wmonths) {
        const a = byMonth.get(m); if (!a) continue;
        total += a.total; shoukai += a.shoukai; yotei += a.yotei; hp += a.hp;
      }
      return {
        total: total / wmonths.length,
        shoukaiRate: total > 0 ? shoukai / total : null,    // 率: 期間プール (Σ紹介/Σ総数)
        yoteiRate: total > 0 ? yotei / total : null,
        hpRate: total > 0 ? hp / total : null,
      };
    };
    return { cur: calc(months), prev: calc(months.map((m) => monthShift(m, -12))) };
  }, [data, months, monthsWithData]);

  const periodLabel = months.length === 1
    ? `${months[0].slice(2, 4)}年${months[0].slice(5, 7)}月`
    : `${months[0].slice(2, 4)}年${months[0].slice(5, 7)}月〜${months.at(-1)!.slice(2, 4)}年${months.at(-1)!.slice(5, 7)}月 月平均`;
  const isPeriodAverage = months.length > 1;

  return (
    <section>
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-base font-semibold">
          🚪 {scopeLabel} 入院経路サマリ ({periodLabel})
        </h2>
        <span className="text-xs text-slate-500">
          新規入院全件 (047_adm_henkansumi 由来、keiro データ取得月のみ)
        </span>
      </div>
      {/* 並び順は 2026-09-18 ユーザ指示: 新規入院数 → 予定入院率 → 病院経由率 → 紹介入院率。
          route-kpi-grid は印刷で 4 列 1 行に固定するための hook。★A4 横 (紙幅 1085px) では
          `md:` (768px) が効くので Tailwind だけでも 4 列になる。固定は紙のサイズ設定が
          変わったとき (A4 縦 = 743px 等) に 2 列へ落ちないための保険。 */}
      <div className="route-kpi-grid grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard title="新規入院数" unit="人" current={cards?.cur?.total ?? null}
                 previous={cards?.prev?.total ?? null} kind="count" avgMode={isPeriodAverage} />
        <KpiCard title="予定入院率" unit="%" current={cards?.cur?.yoteiRate ?? null}
                 previous={cards?.prev?.yoteiRate ?? null} kind="rate" avgMode={isPeriodAverage} />
        <KpiCard title="病院経由率" unit="%" current={cards?.cur?.hpRate ?? null}
                 previous={cards?.prev?.hpRate ?? null} kind="rate" avgMode={isPeriodAverage} />
        <KpiCard title="紹介入院率" unit="%" current={cards?.cur?.shoukaiRate ?? null}
                 previous={cards?.prev?.shoukaiRate ?? null} kind="rate" avgMode={isPeriodAverage} />
      </div>
    </section>
  );
}

function KpiCard({ title, unit, current, previous, kind, avgMode }: {
  title: string; unit: string;
  current: number | null; previous: number | null;
  kind: "count" | "rate"; avgMode?: boolean;
}) {
  const mainStr = current == null ? "—"
    : kind === "rate" ? `${(current * 100).toFixed(1)}`
    : avgMode ? current.toLocaleString(undefined, { maximumFractionDigits: 1 }) : fmtInt(current);
  const prevStr = previous == null ? "—"
    : kind === "rate" ? `${(previous * 100).toFixed(1)} %`
    : avgMode ? previous.toLocaleString(undefined, { maximumFractionDigits: 1 }) : fmtInt(previous);

  let diffStr = "—", diffColor = "text-slate-400";
  if (current != null && previous != null && previous !== 0) {
    if (kind === "rate") {
      const ppt = (current - previous) * 100;
      const sign = ppt > 0 ? "+" : "";
      diffStr = `${sign}${ppt.toFixed(1)}pt`;
      diffColor = ppt >= 1 ? "text-emerald-700" : ppt <= -1 ? "text-red-700" : "text-slate-600";
    } else {
      const ratio = (current - previous) / Math.abs(previous);
      const sign = ratio > 0 ? "+" : "";
      diffStr = `${sign}${(ratio * 100).toFixed(1)}%`;
      diffColor = ratio >= 0.05 ? "text-emerald-700" : ratio <= -0.05 ? "text-red-700" : "text-slate-600";
    }
  }

  return (
    <div className="kpi-card relative">
      <div className="kpi-label">{title} <span className="text-slate-400 font-normal">({unit})</span></div>
      <div className="flex items-baseline">
        <div className="kpi-value">{mainStr}</div>
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{avgMode ? "前年同期" : "前年同月"} {prevStr}</div>
      <div className={`text-xs ${diffColor} font-semibold mt-0.5`}>
        {avgMode ? "前年同期比" : "前年同月比"} {diffStr}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ⑤ 診療科×月: 新規入院数の今年度/前年度比較 + 選択期間の月平均
// ─────────────────────────────────────────────────────────────────────
function RouteDeptMonthlyTable({ data, months, monthsWithData, scopeLabel }: {
  data: RouteResponse | undefined;
  months: string[];
  monthsWithData: Set<string>;
  scopeLabel: string;
}) {
  type DeptMonthRow = {
    診療科: string;
    current: Map<string, number>;
    previous: Map<string, number>;
    currentMean: number | null;
    previousMean: number | null;
    yoyRatio: number | null;
  };

  const tableRows = useMemo<DeptMonthRow[]>(() => {
    if (!data) return [];
    const empty = () => ({ current: new Map<string, number>(), previous: new Map<string, number>() });
    const byDept = new Map<string, ReturnType<typeof empty>>();
    const total = empty();
    const add = (target: ReturnType<typeof empty>, r: RouteRow) => {
      if (months.includes(r.date)) {
        target.current.set(r.date, (target.current.get(r.date) ?? 0) + r.count);
      }
      const currentDate = monthShift(r.date, 12);
      if (months.includes(currentDate)) {
        target.previous.set(currentDate, (target.previous.get(currentDate) ?? 0) + r.count);
      }
    };
    data.rows.forEach((r) => add(total, r));
    (data.byDept ?? []).forEach((r) => {
      const target = byDept.get(r.診療科) ?? (() => {
        const x = empty(); byDept.set(r.診療科, x); return x;
      })();
      add(target, r);
    });

    // 月平均の前年比は、今年度・前年度とも経路データがある月だけで比較する。
    const pairedMonths = months.filter((m) =>
      monthsWithData.has(m) && monthsWithData.has(monthShift(m, -12)));
    const toRow = (診療科: string, cells: ReturnType<typeof empty>): DeptMonthRow => {
      const mean = (field: "current" | "previous") => {
        if (pairedMonths.length === 0) return null;
        return pairedMonths.reduce((s, m) => s + (cells[field].get(m) ?? 0), 0) / pairedMonths.length;
      };
      const currentMean = mean("current");
      const previousMean = mean("previous");
      const yoyRatio = currentMean != null && previousMean != null && previousMean !== 0
        ? currentMean / previousMean : null;
      return { 診療科, ...cells, currentMean, previousMean, yoyRatio };
    };
    const depts = [...byDept].map(([dept, cells]) => toRow(dept, cells));
    depts.sort((a, b) => (b.currentMean ?? -Infinity) - (a.currentMean ?? -Infinity));
    return [toRow("病院全体", total), ...depts];
  }, [data, months, monthsWithData]);

  const s = useSort(tableRows, { pin: (r) => r.診療科 === "病院全体" });
  const label = (date: string) => {
    const [y, m] = date.split("-");
    const n = Number(m);
    return n === 4 || n === 1 ? `${y.slice(2)}/${n}月` : `${n}月`;
  };
  const cellBg = (cur: number | null, prev: number | null) => {
    if (cur == null || prev == null || prev === 0) return "";
    const d = (cur - prev) / Math.abs(prev);
    return d >= .10 ? "bg-emerald-300" : d >= .05 ? "bg-emerald-100"
      : d <= -.10 ? "bg-red-300" : d <= -.05 ? "bg-red-100" : "";
  };
  const fmt = (v: number | null) => v == null ? "—" : Math.round(v).toLocaleString();
  const csvRows = tableRows.map((r) => {
    const out: Record<string, string | number> = {
      診療科: r.診療科,
      月平均: r.currentMean ?? "",
      前年月平均: r.previousMean ?? "",
      "前年比%": r.yoyRatio != null ? Number((r.yoyRatio * 100).toFixed(1)) : "",
    };
    months.forEach((m) => {
      out[`${m.slice(0, 7)}_今年度`] = monthsWithData.has(m) ? (r.current.get(m) ?? 0) : "";
      out[`${m.slice(0, 7)}_前年度`] = monthsWithData.has(monthShift(m, -12)) ? (r.previous.get(m) ?? 0) : "";
    });
    return out;
  });

  return (
    /* route-dept-monthly-sec: 印刷で行を詰めて 22 行を A4 横 1 枚に収める hook (styles.css) */
    <section className="route-dept-monthly-sec card p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">📅 {scopeLabel} 診療科別 新規入院数の月次推移</h2>
        <span className="text-xs text-slate-500">上段=今年度、下段=(前年度同月) / 月平均=選択期間</span>
        <CsvButton rows={csvRows as Record<string, unknown>[]} filename={`route_dept_monthly_${months[0]?.slice(0, 7) ?? "x"}-${months.at(-1)?.slice(0, 7) ?? "x"}`} style={{ marginLeft: "auto" }} />
      </div>
      <div className="text-[10px] text-slate-500 mb-1">セル色（対前年同月）: 緑=増加、赤=減少（淡色±5%以上、濃色±10%以上）</div>
      <div className="overflow-x-auto text-xs">
        <table className="w-full border-collapse">
          <thead className="bg-slate-100 sticky top-0 z-10">
            <tr>
              <SortTh s={s} sortKey="dept" get={(r) => (r as DeptMonthRow).診療科} className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20">診療科</SortTh>
              {months.map((m) => <SortTh key={m} s={s} sortKey={`month:${m}`} get={(r) => (r as DeptMonthRow).current.get(m) ?? null} className="text-right px-1.5 py-1 border-b border-slate-200 tabular-nums">{label(m)}</SortTh>)}
              <SortTh s={s} sortKey="mean" get={(r) => (r as DeptMonthRow).currentMean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">月平均</SortTh>
              <SortTh s={s} sortKey="prevMean" get={(r) => (r as DeptMonthRow).previousMean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">前年平均</SortTh>
              <SortTh s={s} sortKey="yoy" get={(r) => (r as DeptMonthRow).yoyRatio} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">前年比</SortTh>
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r, i) => (
              <tr key={r.診療科} className={`border-b border-slate-100 hover:bg-blue-50/40 ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                <td className={`text-left px-2 py-0.5 sticky left-0 z-[1] font-medium ${i % 2 ? "bg-slate-50" : "bg-white"}`}>{r.診療科}</td>
                {months.map((m) => {
                  const cur = monthsWithData.has(m) ? (r.current.get(m) ?? 0) : null;
                  const prevMonth = monthShift(m, -12);
                  const prev = monthsWithData.has(prevMonth) ? (r.previous.get(m) ?? 0) : null;
                  return <td key={m} className={`text-right px-1.5 py-0.5 tabular-nums leading-tight ${cellBg(cur, prev)}`} title={`${label(m)}\n今年度: ${fmt(cur)}人\n前年度: ${fmt(prev)}人`}>
                    <div>{fmt(cur)}</div><div className="text-slate-500">({fmt(prev)})</div>
                  </td>;
                })}
                <td className={`text-right px-2 py-0.5 tabular-nums leading-tight font-semibold bg-slate-50 ${cellBg(r.currentMean, r.previousMean)}`}>
                  <div>{fmt(r.currentMean)}</div><div className="text-slate-500 font-normal">({fmt(r.previousMean)})</div>
                </td>
                <td className="text-right px-2 py-0.5 tabular-nums bg-slate-50 text-slate-600">{fmt(r.previousMean)}</td>
                <td className="text-right px-2 py-0.5 tabular-nums bg-slate-50 font-semibold">{r.yoyRatio != null ? `${(r.yoyRatio * 100).toFixed(1)}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ② RouteAxisTable: 軸切替 × 月次 × 構成比 × 前年比
// ─────────────────────────────────────────────────────────────────────
function RouteAxisTable({
  axis, setAxis, monthlyByCat, monthTotals, months, monthsWithData, asOfMonth,
  avgMode, allMonths, scopeLabel, sqlMs, isLoading,
}: {
  axis: AxisKey; setAxis: (a: AxisKey) => void;
  monthlyByCat: Map<string, Map<string, number>>;
  monthTotals: Map<string, number>;
  months: string[]; monthsWithData: Set<string>;
  asOfMonth: string; avgMode: boolean; allMonths: string[]; scopeLabel: string;
  sqlMs: number | null; isLoading: boolean;
}) {
  const categories = AXIS_DEFS.find(a => a.key === axis)!.categories;
  // 表示セル: avgMode は末尾12ヶ月移動平均、 そうでなければ単月 (present でなければ null)。
  // 月平均/前年/構成比/対前年 の summary 列は raw のまま (= トグルで変わらない真の月平均)。
  const present = (m: string) => monthsWithData.has(m);
  const cellCat = (mo: string, cat: string) =>
    trailing12(mo, allMonths, avgMode, present, (m) => monthlyByCat.get(m)?.get(cat) ?? 0);
  const cellTot = (mo: string) =>
    trailing12(mo, allMonths, avgMode, present, (m) => monthTotals.get(m) ?? 0);
  const fmtV = (x: number) => avgMode ? x.toFixed(1) : x.toLocaleString();

  // 各カテゴリの 月平均件数 / 前年月平均 / 構成比 / 対前年比
  //
  // Codex review H3 (1周目): 分母を「データある月数」 (monthsWithData ∩ months) に固定。
  //   旧実装は「カテゴリ行が存在した月数」 を分母にしていたため、 稀なカテゴリほど平均過大。
  //   keiro 未取得月は monthsWithData にも入らないので分母から除外、 存在月で
  //   カテゴリゼロ件は 0 加算 → 公正な平均。
  //
  // Codex review M2 (2周目): yoyRatio は「当期/前年 とも データある月」のペア集合で揃える。
  //   月平均そのものは validMonths / prevValidMonths を使うので比較先によって分母が違う。
  //   yoy 比較用に pairedMonths (当期 m AND 前年 m-12 ともデータあり) を別途計算。
  const validMonths = useMemo(() => months.filter((d) => monthsWithData.has(d)), [months, monthsWithData]);
  const prevValidMonths = useMemo(
    () => months.filter((d) => monthsWithData.has(monthShift(d, -12))),
    [months, monthsWithData]
  );
  const pairedMonths = useMemo(
    () => months.filter((d) =>
      monthsWithData.has(d) && monthsWithData.has(monthShift(d, -12))
    ),
    [months, monthsWithData]
  );
  const summary = useMemo(() => {
    // 全体 月平均合計 (anchor scope の合計の平均、 分母 = validMonths)
    const totalMean = validMonths.length > 0
      ? validMonths.reduce((acc, d) => acc + (monthTotals.get(d) ?? 0), 0) / validMonths.length
      : null;
    return categories.map((cat) => {
      const mean = validMonths.length > 0
        ? validMonths.reduce((acc, d) => acc + (monthlyByCat.get(d)?.get(cat) ?? 0), 0) / validMonths.length
        : null;
      const prevMean = prevValidMonths.length > 0
        ? prevValidMonths.reduce((acc, d) => {
            const prev = monthShift(d, -12);
            return acc + (monthlyByCat.get(prev)?.get(cat) ?? 0);
          }, 0) / prevValidMonths.length
        : null;
      // yoyRatio: pairedMonths でフェアに比較
      let yoyRatio: number | null = null;
      if (pairedMonths.length > 0) {
        const curSum = pairedMonths.reduce(
          (a, d) => a + (monthlyByCat.get(d)?.get(cat) ?? 0), 0);
        const prevSum = pairedMonths.reduce(
          (a, d) => a + (monthlyByCat.get(monthShift(d, -12))?.get(cat) ?? 0), 0);
        if (prevSum !== 0) yoyRatio = curSum / prevSum;
      }
      const shareMean = (mean != null && totalMean != null && totalMean > 0) ? mean / totalMean : null;
      return { cat, mean, prevMean, shareMean, yoyRatio };
    });
  }, [categories, validMonths, prevValidMonths, pairedMonths, monthlyByCat, monthTotals]);

  // クリックソート(既定は categories 定義順=summary 原順を維持)。動的な月列もセル値でソート可。
  // 合計行は表下部の別 <tr> なので pin 不要。
  type SummaryRow = typeof summary[number];
  const s = useSort(summary);

  // Csv 用 (Codex L 2周目: 欠損月は 0 ではなく空欄で 画面表示と意味を揃える)
  const tableCsv = useMemo(() => {
    return summary.map((r) => {
      const row: Record<string, string | number> = {
        カテゴリ: r.cat,
        月平均: r.mean ?? "",
        月平均前年: r.prevMean ?? "",
        構成比: r.shareMean != null ? Number((r.shareMean * 100).toFixed(1)) : "",
        対前年: r.yoyRatio != null ? Number((r.yoyRatio * 100).toFixed(1)) : "",
      };
      months.forEach((d) => {
        row[d] = monthsWithData.has(d) ? (monthlyByCat.get(d)?.get(r.cat) ?? 0) : "";
      });
      return row;
    });
  }, [summary, months, monthsWithData, monthlyByCat]);

  const firstMonth = months[0];
  const monthLabel = (s: string) => {
    const [y, mo] = s.split("-");
    const mi = parseInt(mo, 10);
    return s === firstMonth || mi === 1 ? `${y.slice(2)}/${mi}月` : `${mi}月`;
  };

  const cellBg = (pct: number | null): string => {
    if (pct == null) return "";
    if (pct >= 0.10) return "bg-emerald-300";
    if (pct >= 0.05) return "bg-emerald-100";
    if (pct >= -0.05) return "";
    if (pct >= -0.10) return "bg-red-100";
    return "bg-red-300";
  };

  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">
          📋 {scopeLabel} 月次集計 — {AXIS_DEFS.find(a => a.key === axis)!.label}
        </h2>
        {avgMode && <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200">各月セル=末尾12ヶ月移動平均</span>}
        {sqlMs != null && <span className="perf-pill">SQL {sqlMs} ms</span>}
        {isLoading && <span className="text-xs text-slate-400">loading…</span>}
        <CsvButton
          rows={tableCsv as unknown as Record<string, unknown>[]}
          filename={`route_${axis}_${months[0]?.slice(0, 7) ?? "x"}-${asOfMonth.slice(0, 7)}`}
          style={{ marginLeft: "auto" }}
        />
      </div>
      {/* 軸切替 セグコン */}
      <div className="flex items-center gap-1 mb-2">
        <span className="text-xs text-slate-500 mr-1">軸:</span>
        {AXIS_DEFS.map((a) => (
          <button key={a.key}
            onClick={() => setAxis(a.key)}
            className={[
              "text-xs px-2 py-1 rounded border",
              axis === a.key
                ? "bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold"
                : "border-slate-200 text-slate-600 hover:bg-slate-50",
            ].join(" ")}
          >{a.label}</button>
        ))}
      </div>
      {months.length === 0 ? (
        <div className="text-xs text-slate-400 py-8 text-center">データなし</div>
      ) : (
        <div className="overflow-x-auto text-xs">
          <table className="w-full border-collapse">
            <thead className="bg-slate-100 sticky top-0 z-10">
              <tr>
                <SortTh s={s} sortKey="cat" get={(r) => (r as SummaryRow).cat} className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20">カテゴリ</SortTh>
                {months.map((mo) => (
                  <SortTh key={mo} s={s} sortKey={`month:${mo}`} get={(r) => cellCat(mo, (r as SummaryRow).cat)} className="text-right px-1 py-1 border-b border-slate-200 tabular-nums">{monthLabel(mo)}</SortTh>
                ))}
                <SortTh s={s} sortKey="mean" get={(r) => (r as SummaryRow).mean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">月平均</SortTh>
                <SortTh s={s} sortKey="prevMean" get={(r) => (r as SummaryRow).prevMean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">月平均(前年)</SortTh>
                <SortTh s={s} sortKey="shareMean" get={(r) => (r as SummaryRow).shareMean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">構成比</SortTh>
                <SortTh s={s} sortKey="yoyRatio" get={(r) => (r as SummaryRow).yoyRatio} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">対前年</SortTh>
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r) => {
                const yoyDelta = r.yoyRatio != null ? r.yoyRatio - 1 : null;
                return (
                  <tr key={r.cat} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="text-left px-2 py-0.5 sticky left-0 z-[1] font-medium bg-white">{r.cat}</td>
                    {months.map((mo) => {
                      const hasData = monthsWithData.has(mo);
                      // Codex M3: keiro 未取得月は「—」 (空欄)、 データある月で カテゴリゼロは "0"。
                      // avgMode は各月セルを末尾12ヶ月移動平均に置換。
                      const v = cellCat(mo, r.cat);
                      const total = cellTot(mo) ?? 0;
                      const share = (v != null && total > 0) ? v / total : null;
                      return (
                        <td key={mo} className={`text-right px-1 py-0.5 tabular-nums leading-tight ${hasData ? "" : "bg-amber-50/40 text-slate-300"}`}
                            title={hasData
                              ? `${monthLabel(mo)}${avgMode ? " (末尾12ヶ月移動平均)" : ""}\n件数: ${v != null ? fmtV(v) : "—"}\n構成比: ${share != null ? fmtPct(share) : "—"}\n月合計: ${fmtV(total)}`
                              : `${monthLabel(mo)}: keiro データ未取得 (除外)`}>
                          {v != null ? (
                            <>
                              <div>{fmtV(v)}</div>
                              {share != null && (
                                <div className="text-[9px] text-slate-400 font-normal">{(share * 100).toFixed(1)}%</div>
                              )}
                            </>
                          ) : "—"}
                        </td>
                      );
                    })}
                    <td className="text-right px-2 py-0.5 tabular-nums font-semibold bg-slate-50">
                      {r.mean != null ? r.mean.toFixed(1) : "—"}
                    </td>
                    <td className="text-right px-2 py-0.5 tabular-nums text-slate-500 bg-slate-50">
                      {r.prevMean != null ? r.prevMean.toFixed(1) : "—"}
                    </td>
                    <td className="text-right px-2 py-0.5 tabular-nums font-semibold bg-slate-50">
                      {r.shareMean != null ? `${(r.shareMean * 100).toFixed(1)}%` : "—"}
                    </td>
                    <td className={`text-right px-2 py-0.5 tabular-nums font-semibold ${cellBg(yoyDelta)}`}>
                      {r.yoyRatio != null ? `${(r.yoyRatio * 100).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                );
              })}
              {/* 月合計行 */}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                <td className="text-left px-2 py-1 sticky left-0 z-[1] bg-slate-50">合計</td>
                {months.map((mo) => {
                  const hasData = monthsWithData.has(mo);
                  const tv = cellTot(mo);
                  return (
                    <td key={mo} className={`text-right px-1 py-1 tabular-nums ${hasData ? "" : "text-slate-300"}`}>
                      {tv != null ? fmtV(tv) : "—"}
                    </td>
                  );
                })}
                <td colSpan={4} className="text-right px-2 py-1 tabular-nums text-slate-500">
                  月平均合計: {(() => {
                    // Codex H3: 分母 = validMonths.length (= データある月数)
                    if (validMonths.length === 0) return "—";
                    const s = validMonths.reduce((a, d) => a + (monthTotals.get(d) ?? 0), 0);
                    return (s / validMonths.length).toFixed(1);
                  })()} 人 (n={validMonths.length} ヶ月)
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ③ RouteMixStackedBar: 月次 積上 (件数 / 構成比 toggle)
// ─────────────────────────────────────────────────────────────────────
function RouteMixStackedBar({
  axis, categories, colors, monthlyByCat, monthTotals, months, monthsWithData,
  avgMode, allMonths, stackMode, setStackMode, scopeLabel,
}: {
  axis: AxisKey; categories: string[]; colors: string[];
  monthlyByCat: Map<string, Map<string, number>>;
  monthTotals: Map<string, number>;
  months: string[]; monthsWithData: Set<string>;
  avgMode: boolean; allMonths: string[];
  stackMode: "件数" | "構成比"; setStackMode: (m: "件数" | "構成比") => void;
  scopeLabel: string;
}) {
  const present = (m: string) => monthsWithData.has(m);
  // long 化 (date × category × value)
  //
  // Codex 2 周目 M: 旧実装は monthlyByCat に行ない月を skip していたため、
  //   グラフから欠損月が消えていた (表側は修正済みだが viz 側だけ未解消)。
  //   修正: displayMonths (= 要求期間の全月) を強制使用し、 欠損月はバーゼロで保持。
  // avgMode: 各月の値を末尾12ヶ月移動平均に置換 (行為別/手術タブと統一)。
  type Pt = { date: Date; ym: string; cat: string; value: number; share: number };
  const data = useMemo<Pt[]>(() => {
    const out: Pt[] = [];
    months.forEach((d) => {
      const total = trailing12(d, allMonths, avgMode, present, (m) => monthTotals.get(m) ?? 0) ?? 0;
      categories.forEach((cat) => {
        const v = trailing12(d, allMonths, avgMode, present, (m) => monthlyByCat.get(m)?.get(cat) ?? 0) ?? 0;
        out.push({
          date: new Date(d), ym: d.slice(0, 7), cat,
          value: v,
          share: total > 0 ? v / total : 0,
        });
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months, categories, monthlyByCat, monthTotals, avgMode, allMonths, monthsWithData]);

  // x 軸 domain は requested period 全月 (= displayMonths) で固定。
  const monthList = useMemo(() => months.map((d) => d.slice(0, 7)), [months]);

  const plotOpts = useMemo<Plot.PlotOptions>(() => {
    const yField: "value" | "share" = stackMode === "件数" ? "value" : "share";
    const fmtV = (x: number) => avgMode ? x.toFixed(1) : x.toLocaleString();
    // 各月の合計 (件数) を バー上部ラベル用に整形 (2026-06-15 ユーザ要望)。 avgMode=移動平均。
    const totalsData = monthList.map((ym) => ({
      ym,
      total: trailing12(`${ym}-01`, allMonths, avgMode, present, (m) => monthTotals.get(m) ?? 0) ?? 0,
    }));
    return {
      width: 1100, height: 320,
      marginLeft: 60, marginRight: 140, marginTop: 30, marginBottom: 34,
      style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "11px" },
      x: { label: null, type: "band", domain: monthList },
      y: {
        label: stackMode === "件数" ? "件数" : "構成比",
        grid: true,
        tickFormat: stackMode === "件数"
          ? ((v: number) => v.toLocaleString())
          : ((v: number) => `${(v * 100).toFixed(0)}%`),
      },
      color: { domain: categories, range: colors, legend: true },
      marks: [
        Plot.barY(data, { x: "ym", y: yField, fill: "cat", z: "cat", order: categories }),
        // segment 中央に件数 / 構成比 を表示。
        // stackY transform は z channel (= 系列識別) が必須。 fill は カテゴリ色の luminance に応じて
        // 白 or 濃灰 を自動切替 (Codex 配色 review 後の contrast 改善)。
        Plot.text(data.filter((d) => d.value > 0), Plot.stackY({
          x: "ym", y: yField, z: "cat", order: categories,
          text: (d: Pt) => {
            // 総CL緊急 は帯が細くラベルを置くスペースが無いので非表示 (2026-06-15 ユーザ要望)
            if (d.cat.startsWith("総CL緊急")) return "";
            // 件数モードは 件数(上段) + 構成比%(下段) の 2 行 (2026-06-15 ユーザ要望)。
            // \n で Plot が自動的に上下 2 行に中央寄せ描画する。
            return stackMode === "件数"
              ? (d.value >= 10 ? `${fmtV(d.value)}\n${(d.share * 100).toFixed(0)}%` : "")
              : (d.share >= 0.05 ? `${(d.share * 100).toFixed(0)}%` : "");
          },
          fill: (d: Pt) => labelColorOf(axis, d.cat),
          fontSize: 12, fontWeight: "bold",   // 2026-06-15: 9→12 (棒内数字を大きく)
        })),
        // 月合計を各バーの上に表示 (2026-06-15 ユーザ要望)。件数モードはバー頂点 (y=total)、
        // 構成比モードは正規化バー頂点 (y=1.0) の上に件数合計を出す。
        Plot.text(totalsData.filter((d) => d.total > 0), {
          x: "ym",
          y: (d: { total: number }) => (stackMode === "件数" ? d.total : 1),
          text: (d: { total: number }) => fmtV(d.total),
          lineAnchor: "bottom", dy: -4,
          fontSize: 13, fontWeight: "bold", fill: "#0f172a",
        }),
        // Codex L1: stackY 後の位置と素 yField で pointer 判定するとずれるので、
        //   pointerX で月単位 hover にし、 その月の内訳を全カテゴリ列挙する形に。
        Plot.tip(monthList.map((ym) => ({ ym })), Plot.pointerX({
          x: "ym",
          frameAnchor: "top",
          title: (d: { ym: string }) => {
            const t = trailing12(`${d.ym}-01`, allMonths, avgMode, present, (m) => monthTotals.get(m) ?? 0) ?? 0;
            const lines = categories.map((cat) => {
              const v = trailing12(`${d.ym}-01`, allMonths, avgMode, present, (m) => monthlyByCat.get(m)?.get(cat) ?? 0) ?? 0;
              const share = t > 0 ? (v / t * 100).toFixed(1) : "—";
              return `${cat}: ${fmtV(v)} (${share}%)`;
            });
            return `${d.ym}${avgMode ? " (末尾12ヶ月移動平均)" : ""}\n月合計: ${fmtV(t)}\n${lines.join("\n")}`;
          },
        })),
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, categories, colors, stackMode, monthList, monthTotals, monthlyByCat, avgMode, allMonths, monthsWithData]);

  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">
          📊 {scopeLabel} {avgMode ? "直近12ヶ月移動平均" : "月次"} 経路 構成 — {AXIS_DEFS.find(a => a.key === axis)!.label}
        </h2>
        {avgMode && <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200">各月=末尾12ヶ月移動平均</span>}
        <div className="ml-auto flex items-center gap-1">
          <span className="text-xs text-slate-500 mr-1">表示:</span>
          {(["件数", "構成比"] as const).map((m) => (
            <button key={m}
              onClick={() => setStackMode(m)}
              className={[
                "text-xs px-2 py-1 rounded border",
                stackMode === m
                  ? "bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50",
              ].join(" ")}
            >{m}</button>
          ))}
        </div>
      </div>
      <PlotFigure options={plotOpts} responsiveWidth />
      {(axis === "経路" || axis === "経路7") && (
        <div className="text-[11px] text-slate-400 mt-1 leading-tight">
          色の規則: <b>濃色＝予定 / 淡色＝緊急</b>、 色相(hue)＝経由先（病院 / 総CL / その他）
          {axis === "経路7" ? "、 さらに紹介有無で濃淡を分割" : ""}。
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ④ RouteDeptTable: 科 × 軸カテゴリ (期間累計件数 + 構成比 + ランキング)
// ─────────────────────────────────────────────────────────────────────
function RouteDeptTable({
  data, months, monthsWithData, axis, categories,
}: {
  data: RouteResponse | undefined;
  months: string[];
  monthsWithData: Set<string>;
  axis: AxisKey;
  categories: string[];
}) {
  // byDept 行を期間でフィルタして、 科 × カテゴリ で集計
  const deptCells = useMemo(() => {
    if (!data?.byDept || months.length === 0) return [];
    const validMonths = months.filter((m) => monthsWithData.has(m));
    const monthSet = new Set(validMonths);
    const divisor = validMonths.length;
    if (divisor === 0) return [];
    const m = new Map<string, Map<string, number>>(); // dept → cat → count
    for (const r of data.byDept) {
      if (!monthSet.has(r.date)) continue;
      const cat = categoryOf(axis, r);
      if (!m.has(r.診療科)) m.set(r.診療科, new Map());
      const cm = m.get(r.診療科)!;
      cm.set(cat, (cm.get(cat) ?? 0) + r.count);
    }
    // 科ごとに total + cells 整形
    type Row = { 診療科: string; total: number; cells: Map<string, number>; shoukai?: number; yotei?: number };
    const rows: Row[] = [];
    m.forEach((cm, dept) => {
      let total = 0;
      cm.forEach((v, cat) => { cm.set(cat, v / divisor); total += v / divisor; });
      // 紹介率/予定率/経由HP比率を共通で計算 (常に表示するため byDept 全件で別ループ)
      rows.push({ 診療科: dept, total, cells: cm });
    });
    // 紹介/予定/経由HP の別集計 (axis 切替に依存しない常時表示)
    const ratios = new Map<string, { shoukai: number; yotei: number; hp: number; total: number }>();
    for (const r of data.byDept) {
      if (!monthSet.has(r.date)) continue;
      if (!ratios.has(r.診療科)) ratios.set(r.診療科, { shoukai: 0, yotei: 0, hp: 0, total: 0 });
      const x = ratios.get(r.診療科)!;
      x.total += r.count;
      if (r.紹介 === 1) x.shoukai += r.count;
      if (r.予定緊急 === "予定") x.yotei += r.count;
      if (r.経由 === "HP") x.hp += r.count;
    }
    return rows
      .map((r) => {
        const x = ratios.get(r.診療科);
        return {
          ...r,
          shoukaiRate: x && x.total > 0 ? x.shoukai / x.total : null,
          yoteiRate:   x && x.total > 0 ? x.yotei / x.total   : null,
          hpRate:      x && x.total > 0 ? x.hp / x.total      : null,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [data, months, monthsWithData, axis]);

  const f = useFilters();
  // クリックソート(既定は total 降順=deptCells 原順を維持)。動的な経路カテゴリ列も個別ソート可。
  type DeptRow = typeof deptCells[number];
  const s = useSort(deptCells);
  const periodLabel = months.length === 1
    ? `${months[0].slice(2, 4)}年${months[0].slice(5, 7)}月`
    : `${months[0].slice(2, 4)}年${months[0].slice(5, 7)}月〜${months.at(-1)!.slice(2, 4)}年${months.at(-1)!.slice(5, 7)}月 月平均`;
  const fmtCount = (v: number) => months.length > 1
    ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) : Math.round(v).toLocaleString();

  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h2 className="text-base font-semibold">
          🏥 診療科別 経路内訳 ({periodLabel}、{AXIS_DEFS.find(a => a.key === axis)!.label})
        </h2>
        <span className="text-xs text-slate-500">
          各科クリックで 上部 KPI/集計/積上 がその科にフォーカス
        </span>
      </div>
      {deptCells.length === 0 ? (
        <div className="text-xs text-slate-400 py-8 text-center">データなし</div>
      ) : (
        <div className="overflow-x-auto text-xs">
          <table className="w-full border-collapse">
            <thead className="bg-slate-100">
              <tr>
                <SortTh s={s} sortKey="dept" get={(r) => (r as DeptRow).診療科} className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-10">診療科</SortTh>
                <SortTh s={s} sortKey="total" get={(r) => (r as DeptRow).total} className="text-right px-2 py-1 border-b border-slate-200">新規入院 月平均</SortTh>
                {categories.map((c) => (
                  <SortTh key={c} s={s} sortKey={`cat:${c}`} get={(r) => (r as DeptRow).cells.get(c) ?? 0} className="text-right px-1 py-1 border-b border-slate-200 whitespace-nowrap">{c} (構成比)</SortTh>
                ))}
                <SortTh s={s} sortKey="shoukai" get={(r) => (r as DeptRow).shoukaiRate} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">紹介率</SortTh>
                <SortTh s={s} sortKey="yotei" get={(r) => (r as DeptRow).yoteiRate} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">予定率</SortTh>
                <SortTh s={s} sortKey="hp" get={(r) => (r as DeptRow).hpRate} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">病院経由</SortTh>
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r) => {
                const isFocused = f.focusedDept === r.診療科;
                return (
                  <tr key={r.診療科}
                      className={isFocused
                        ? "border-y-2 border-amber-500"
                        : "border-b border-slate-100 hover:bg-slate-50"}>
                    <td className={`text-left px-2 py-0.5 sticky left-0 z-[1] font-medium bg-white ${isFocused ? "border-l-2 border-amber-500" : ""}`}>
                      <button
                        onClick={() => f.setFocusedDept(isFocused ? null : r.診療科)}
                        className={`text-left ${isFocused ? "font-bold" : "hover:underline decoration-dotted"}`}
                        title={isFocused ? "クリックでフォーカス解除" : "この診療科にフォーカス"}
                      >
                        {isFocused ? <span className="mr-1">🎯</span> : null}{r.診療科}
                      </button>
                    </td>
                    <td className="text-right px-2 py-0.5 tabular-nums font-semibold">{fmtCount(r.total)}</td>
                    {categories.map((c) => {
                      const v = r.cells.get(c) ?? 0;
                      const share = r.total > 0 ? v / r.total : 0;
                      return (
                        <td key={c} className="text-right px-1 py-0.5 tabular-nums"
                            title={`${c}: 月平均 ${fmtCount(v)} 件 (${(share * 100).toFixed(1)}%)`}>
                          {fmtCount(v)}<span className="text-slate-400 text-[10px] ml-1">({(share * 100).toFixed(0)}%)</span>
                        </td>
                      );
                    })}
                    <td className="text-right px-2 py-0.5 tabular-nums bg-slate-50">{r.shoukaiRate != null ? `${(r.shoukaiRate * 100).toFixed(1)}%` : "—"}</td>
                    <td className="text-right px-2 py-0.5 tabular-nums bg-slate-50">{r.yoteiRate != null ? `${(r.yoteiRate * 100).toFixed(1)}%` : "—"}</td>
                    <td className={`text-right px-2 py-0.5 tabular-nums bg-slate-50 ${isFocused ? "border-r-2 border-amber-500" : ""}`}>{r.hpRate != null ? `${(r.hpRate * 100).toFixed(1)}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// helpers ────────────────────────────────────────────────────────────
function monthShift(yyyymmdd: string, delta: number): string {
  const [y, m, d] = yyyymmdd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, d));
  return [
    dt.getUTCFullYear(),
    String(dt.getUTCMonth() + 1).padStart(2, "0"),
    String(dt.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

// 未使用警告抑止 (将来の月単位 chart 用 import の予約)
void fmtMonthJp; void buildTimeAxis; void buildMonthAxisMark;
